import { useCallback, useEffect, useRef, useState } from "react";
import { isWeb } from "@tamagui/core";
import { Animated, Easing, PanResponder, Platform, Text as RNText } from "react-native-web";
import { Box } from "../Box";
import { ChevronBackButton, ChevronForwardButton } from "../Button";
import { Image } from "../Image/Image";
import { Text } from "../Text/Text";
import { PhotoCarouselDots } from "./PhotoCarouselDots";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
const DEFAULT_ASPECT_RATIO = 16 / 9, SQUARE_ASPECT_RATIO = 1 / 1;
var PhotoCarouselAspectRatios = /* @__PURE__ */ ((PhotoCarouselAspectRatios2) => (PhotoCarouselAspectRatios2[PhotoCarouselAspectRatios2.DEFAULT = DEFAULT_ASPECT_RATIO] = "DEFAULT", PhotoCarouselAspectRatios2[PhotoCarouselAspectRatios2.SQUARE = SQUARE_ASPECT_RATIO] = "SQUARE", PhotoCarouselAspectRatios2))(PhotoCarouselAspectRatios || {});
const PhotoCarousel = ({
  photos,
  children,
  ariaLabel,
  hideNavigation,
  aspectRatio = PhotoCarouselAspectRatios.DEFAULT,
  height = "100%",
  isRounded = !1
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0), [containerWidth, setContainerWidth] = useState(0), [panResponder, setPanResponder] = useState(null), offsetX = useRef(new Animated.Value(0)).current, currentOffset = useRef(0), carouselRef = useRef(null), navigationMessagePosition = useRef(new Animated.Value(0)).current, [showNavigationMessage, setShowNavigationMessage] = useState(!1), useNativeDriver = Platform.OS !== "web", onLayout = (event) => {
    const { width } = event.nativeEvent.layout;
    setContainerWidth(width);
  };
  useEffect(() => {
    Animated.timing(navigationMessagePosition, {
      toValue: showNavigationMessage ? -65 : 0,
      duration: 300,
      easing: Easing.ease,
      useNativeDriver
    }).start();
  }, [navigationMessagePosition, showNavigationMessage, useNativeDriver]), useEffect(() => {
    const responder = PanResponder.create({
      onMoveShouldSetPanResponder: () => !0,
      onPanResponderGrant: () => {
        offsetX.setValue(currentOffset.current);
      },
      onPanResponderMove: (evt, gestureState) => {
        offsetX.setValue(currentOffset.current + gestureState.dx);
      },
      onPanResponderRelease: (evt, gestureState) => {
        const threshold = containerWidth / 7;
        let newIndex = currentImageIndex;
        gestureState.dx > threshold ? newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : currentImageIndex : gestureState.dx < -threshold && (newIndex = currentImageIndex < photos.length - 1 ? currentImageIndex + 1 : currentImageIndex), newIndex !== currentImageIndex ? Animated.timing(offsetX, {
          toValue: -newIndex * containerWidth,
          duration: 300,
          easing: Easing.linear,
          useNativeDriver
        }).start(() => {
          setCurrentImageIndex(newIndex), currentOffset.current = -newIndex * containerWidth;
        }) : Animated.spring(offsetX, {
          toValue: currentOffset.current,
          useNativeDriver
        }).start();
      }
    });
    setPanResponder(responder);
  }, [containerWidth, photos.length, offsetX, currentImageIndex, useNativeDriver]), useEffect(() => {
    offsetX.setValue(-currentImageIndex * containerWidth), currentOffset.current = -currentImageIndex * containerWidth;
  }, [currentImageIndex, containerWidth, offsetX]);
  const renderImages = () => photos.map((photo, index) => index >= currentImageIndex - 2 && index <= currentImageIndex + 2 ? /* @__PURE__ */ jsx(
    Box,
    {
      width: containerWidth,
      height: "100%",
      "aria-hidden": index !== currentImageIndex,
      children: /* @__PURE__ */ jsx(
        Image,
        {
          source: { uri: photo.url },
          alt: photo.alt || `Image ${index + 1}`,
          width: "100%",
          height: "100%"
        }
      )
    },
    index
  ) : /* @__PURE__ */ jsx(Box, { width: containerWidth, height: "100%", "aria-hidden": !0 }, index)), handlePrev = useCallback(() => {
    currentImageIndex > 0 && setCurrentImageIndex(currentImageIndex - 1);
  }, [currentImageIndex]), handleNext = useCallback(() => {
    currentImageIndex < photos.length - 1 && setCurrentImageIndex(currentImageIndex + 1);
  }, [currentImageIndex, photos.length]);
  useEffect(() => {
    if (!isWeb) return;
    const handleKeyDown = (event) => {
      event.key === "ArrowLeft" ? handlePrev() : event.key === "ArrowRight" && handleNext(), showNavigationMessage && setShowNavigationMessage(!1);
    }, carouselContainer = carouselRef.current;
    return carouselContainer?.addEventListener("keydown", handleKeyDown), () => {
      carouselContainer?.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleNext, handlePrev, showNavigationMessage]);
  const atStart = currentImageIndex === 0, atEnd = currentImageIndex === photos.length - 1;
  return /* @__PURE__ */ jsxs(
    Box,
    {
      role: "region",
      "aria-label": ariaLabel || "Photo Carousel",
      tabIndex: 0,
      ref: carouselRef,
      onFocus: (event) => {
        document.activeElement === carouselRef.current && event.relatedTarget !== null && (setShowNavigationMessage(!0), setTimeout(() => {
          setShowNavigationMessage(!1);
        }, 3e3));
      },
      "data-testid": "photo-carousel",
      children: [
        /* @__PURE__ */ jsxs(
          RNText,
          {
            accessibilityRole: "text",
            accessible: !1,
            style: {
              position: "absolute",
              width: 0,
              height: 0,
              overflow: "hidden"
            },
            children: [
              "This is an photo carousel of the property. Swipe left or right to navigate through the photos.",
              " ",
              !hideNavigation && "Alternatively, use the next and previous buttons to move between photos."
            ]
          }
        ),
        /* @__PURE__ */ jsx(Box, { width: "100%", onLayout, ...panResponder && panResponder.panHandlers, children: /* @__PURE__ */ jsxs(
          Box,
          {
            aspectRatio,
            width: "100%",
            position: "relative",
            overflow: "hidden",
            height,
            borderTopLeftRadius: isRounded ? "$16x" : void 0,
            borderTopRightRadius: isRounded ? "$16x" : void 0,
            children: [
              /* @__PURE__ */ jsx(
                Animated.View,
                {
                  style: {
                    flexDirection: "row",
                    width: containerWidth * photos.length,
                    height: "100%",
                    transform: [{ translateX: offsetX }]
                  },
                  children: renderImages()
                }
              ),
              isWeb && !hideNavigation ? /* @__PURE__ */ jsxs(Fragment, { children: [
                !atStart && /* @__PURE__ */ jsx(
                  ChevronBackButton,
                  {
                    size: "medium",
                    position: "absolute",
                    top: "50%",
                    left: "$10x",
                    analyticsName: "",
                    onPress: handlePrev,
                    "aria-label": "Previous photo",
                    "data-testid": "chevron-back-button"
                  }
                ),
                !atEnd && /* @__PURE__ */ jsx(
                  ChevronForwardButton,
                  {
                    size: "medium",
                    position: "absolute",
                    top: "50%",
                    right: "$10x",
                    analyticsName: "",
                    onPress: handleNext,
                    "aria-label": "Next photo",
                    "data-testid": "chevron-forward-button"
                  }
                )
              ] }) : null,
              children,
              /* @__PURE__ */ jsx(
                Box,
                {
                  position: "absolute",
                  bottom: "$10x",
                  width: containerWidth,
                  alignItems: "center",
                  flexDirection: "row",
                  justifyContent: "center",
                  "data-testid": "photo-carousel-dots",
                  children: /* @__PURE__ */ jsx(PhotoCarouselDots, { length: photos.length, currentIndex: currentImageIndex })
                }
              ),
              hideNavigation ? /* @__PURE__ */ jsx(
                Animated.View,
                {
                  style: {
                    position: "absolute",
                    bottom: -30,
                    left: 0,
                    right: 0,
                    justifyContent: "center",
                    flexDirection: "row",
                    transform: [{ translateY: navigationMessagePosition }]
                  },
                  children: /* @__PURE__ */ jsx(
                    Text,
                    {
                      backgroundColor: "$backgroundInversePrimary",
                      color: "$backgroundPrimary",
                      paddingHorizontal: "$4x",
                      paddingVertical: "$2x",
                      borderRadius: "$10x",
                      typography: "$bodySmall",
                      opacity: 0.7,
                      children: "Use arrow keys to navigate"
                    }
                  )
                }
              ) : null
            ]
          }
        ) })
      ]
    }
  );
};
export {
  PhotoCarousel,
  PhotoCarouselAspectRatios
};
